import type { ResponsiveLayouts } from "react-grid-layout";
import type { LayoutItem } from "./type";

/**
 * Capitalizes the first letter of a string
 */
export function capitalize(str: string | null | undefined): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Generates a random integer between min and max (inclusive)
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Generates a random layout item
 */
export function generateRandomLayoutItem(i: number): LayoutItem {
  const y = Math.ceil(Math.random() * 4) + 1;
  return {
    x: (randomInt(0, 5) * 2) % 12,
    y: Math.floor(i / 6) * y,
    w: 2,
    h: y,
    i: i.toString(),
    static: Math.random() < 0.05,
  };
}

/**
 * Creates a layout item from configuration [x, y, w, h]
 */
export function createLayoutItemFromConfig(
  i: number,
  config: [number, number, number, number],
): LayoutItem {
  const [x, y, w, h] = config;
  return {
    x,
    y,
    w,
    h,
    i: i.toString(),
    static: true,
  };
}

/**
 * Generates a layout with predefined configurations for initial items
 * and random configurations for the rest
 */
export function generateLayout(
  predefinedLayouts: Array<[number, number, number, number]>,
  totalItems: number = 9,
): LayoutItem[] {
  const result: LayoutItem[] = [];
  for (let i = 0; i < totalItems; i++) {
    // Use predefined layout for items with configurations
    if (i < predefinedLayouts.length) {
      result.push(createLayoutItemFromConfig(i, predefinedLayouts[i]));
    } else {
      // Generate random layout for the rest
      result.push(generateRandomLayoutItem(i));
    }
  }
  return result;
}

/**
 * Efficiently compares layout positions without JSON.stringify
 */
export function layoutsEqual(
  current: ResponsiveLayouts,
  updated: ResponsiveLayouts,
): boolean {
  const currentKeys = Object.keys(current);
  const updatedKeys = Object.keys(updated);

  if (currentKeys.length !== updatedKeys.length) {
    return false;
  }

  for (const key of currentKeys) {
    const currentLayout = current[key];
    const updatedLayout = updated[key];

    if (!currentLayout || !updatedLayout) {
      if (currentLayout !== updatedLayout) return false;
      continue;
    }

    if (currentLayout.length !== updatedLayout.length) {
      return false;
    }

    for (let i = 0; i < currentLayout.length; i++) {
      const curr = currentLayout[i];
      const upd = updatedLayout[i];
      if (
        curr.x !== upd.x ||
        curr.y !== upd.y ||
        curr.w !== upd.w ||
        curr.h !== upd.h
      ) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Updates layouts with static property - extracted helper to avoid duplication
 */
export function updateLayoutsWithStatic(
  layouts: ResponsiveLayouts,
  allStatic: boolean,
): ResponsiveLayouts {
  const updatedLayouts: ResponsiveLayouts = {};
  for (const breakpoint in layouts) {
    const breakpointLayout = layouts[breakpoint];
    if (breakpointLayout) {
      updatedLayouts[breakpoint] = breakpointLayout.map((item) => ({
        ...item,
        static: allStatic,
      })) as LayoutItem[];
    }
  }
  return updatedLayouts;
}
